import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Grow, Box, Typography, IconButton, Chip, TextField,
  CircularProgress, Divider, Tooltip, Menu, MenuItem,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAdminTickets, useSetTicketStatus } from '../hooks/useSupport';
import SupportChatThread from '../components/common/SupportChatThread';
import DraggableFab from '../components/common/DraggableFab';

const ACCENT2 = '#7c5cff';

const CATEGORY_LABEL = {
  PAYROLL: 'Payroll & Salary', LEAVE: 'Leave & Attendance', DOCUMENTS: 'Documents & Form16',
  IT: 'IT & Access', GENERAL: 'Policy & General',
};

const STATUS_META = {
  OPEN: { label: 'Open', fg: '#b8631f', bg: '#fbf0e4' },
  ANSWERED: { label: 'Answered', fg: '#0f7a72', bg: '#e7f5f3' },
  AWAITING_EMPLOYEE: { label: 'Awaiting employee', fg: '#a23d68', bg: '#f8e9ef' },
  RESOLVED: { label: 'Resolved', fg: '#1f8a5f', bg: '#e8f5ee' },
};

const FILTERS = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'ANSWERED', label: 'Answered' },
  { value: 'AWAITING_EMPLOYEE', label: 'Awaiting employee' },
  { value: 'RESOLVED', label: 'Resolved' },
];

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, fg: '#656b85', bg: '#f1f2f6' };
  return (
    <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700, fontSize: '.68rem', height: 22 }} />
  );
};

const timeAgo = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
};

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

function InboxList({ onOpen }) {
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const { data: tickets = [], isLoading } = useAdminTickets({ status, search: search || undefined }, { refetchInterval: 20000 });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', px: 2, pt: 1.5 }}>
        {FILTERS.map((f) => (
          <Chip
            key={f.value} label={f.label} size="small" onClick={() => setStatus(f.value)}
            sx={{
              fontSize: '.68rem', fontWeight: 600,
              bgcolor: status === f.value ? '#f1edff' : 'transparent',
              color: status === f.value ? '#5c3fd6' : '#656b85',
            }}
          />
        ))}
      </Box>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          fullWidth size="small" placeholder="Search employee, ticket id, or keyword…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '.85rem' } }}
        />
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>}
        {!isLoading && tickets.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: '#9298b2', fontSize: '.82rem', mt: 5 }}>
            No tickets match this filter.
          </Typography>
        )}
        {tickets.map((t) => (
          <Box
            key={t.id}
            onClick={() => onOpen(t.id)}
            sx={{
              p: 1.25, mb: 1, borderRadius: '12px', border: '1px solid #eef0f4', cursor: 'pointer',
              bgcolor: t.hr_unread ? '#f8f6ff' : '#fff',
              '&:hover': { borderColor: ACCENT2 },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '.82rem', fontWeight: t.hr_unread ? 800 : 700 }}>
                {t.employee_name} <Box component="span" sx={{ color: '#9298b2', fontWeight: 500 }}>({t.employee_no})</Box>
              </Typography>
              <StatusChip status={t.status} />
            </Box>
            <Typography sx={{ fontSize: '.74rem', color: '#656b85', mt: .25 }}>
              {CATEGORY_LABEL[t.category] || t.category}
              {t.assigned_hr_name ? ` - Assigned to ${t.assigned_hr_name}` : ''}
            </Typography>
            <Typography sx={{ fontSize: '.8rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t.last_message}
            </Typography>
            <Typography sx={{ fontSize: '.66rem', color: '#9298b2', mt: .4 }}>
              #{t.id} &middot; {timeAgo(t.updated_at)} ago
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ThreadView({ ticketId, onBack, onClose }) {
  const setStatus = useSetTicketStatus();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const applyStatus = async (status) => {
    setMenuAnchor(null);
    await setStatus.mutateAsync({ ticketId, status });
  };

  return (
    <SupportChatThread
      ticketId={ticketId}
      viewerIsHr
      accent={ACCENT2}
      composerPlaceholder="Reply as HR…"
      handoffMode
      onManageStatus={(event) => setMenuAnchor(event.currentTarget)}
      renderHeader={(ticket) => (
        <Box sx={{
          minHeight: 74, p: '13px 12px 13px 18px', borderBottom: '1px solid #eef0f4',
          display: 'flex', alignItems: 'center', gap: '12px', bgcolor: '#fff', color: '#171b2f',
        }}>
          {ticket && (
            <>
              <Box sx={{
                width: 48, height: 48, flex: '0 0 auto', position: 'relative', borderRadius: '50%',
                display: 'grid', placeItems: 'center', color: '#fff', fontSize: '1rem', fontWeight: 850,
                background: `linear-gradient(135deg, ${ACCENT2} 0%, #5c3fd6 100%)`, border: '2px solid #eeeaff',
              }}>
                {initials(ticket.employee_name)}
                <Box sx={{ position: 'absolute', right: -1, bottom: 1, width: 12, height: 12, borderRadius: '50%', bgcolor: '#45d389', border: '2px solid #fff' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '1rem', lineHeight: 1.2, fontWeight: 800 }}>
                  {ticket.employee_name} <Box component="span" sx={{ color: '#9298b2', fontSize: '.72rem', fontWeight: 500 }}>({ticket.employee_no})</Box>
                </Typography>
                <Typography noWrap sx={{ fontSize: '.69rem', color: '#656b85' }}>
                  {CATEGORY_LABEL[ticket.category] || ticket.category} &middot; #{ticket.id}
                </Typography>
                <Typography noWrap sx={{ mt: .2, fontSize: '.66rem', color: '#9298b2' }}>
                  Live support &middot; {(STATUS_META[ticket.status] || { label: ticket.status }).label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
                <IconButton size="small" onClick={onBack} aria-label="Back to support inbox" sx={{ width: 34, height: 34, bgcolor: '#f6f7fc', borderRadius: '10px' }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Change ticket status" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ width: 34, height: 34, bgcolor: '#f6f7fc', borderRadius: '10px' }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={onClose} aria-label="Close support inbox" sx={{ width: 34, height: 34, bgcolor: '#f6f7fc', borderRadius: '10px' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} sx={{ zIndex: 2147483001 }}>
                <MenuItem onClick={() => applyStatus('AWAITING_EMPLOYEE')}>Mark awaiting employee</MenuItem>
                <MenuItem onClick={() => applyStatus('RESOLVED')}>Mark resolved</MenuItem>
                <MenuItem onClick={() => applyStatus('OPEN')}>Reopen</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      )}
    />
  );
}

export default function SupportInboxWidget() {
  const [open, setOpen] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const closeOnOutsidePointer = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      if (event.target.closest?.('[role="menu"]')) return;
      setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => {
      window.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
    };
  }, [open]);

  // Cheap unread badge: pull the OPEN queue only (unread tickets are always
  // OPEN or freshly reopened) rather than polling the whole inbox.
  const { data: openTickets = [] } = useAdminTickets({ status: 'OPEN' }, { refetchInterval: 30000 });
  const unreadCount = useMemo(() => openTickets.filter((t) => t.hr_unread).length, [openTickets]);

  return (
    <>
      {!open && (
        <Tooltip title="Support Inbox — drag to move" placement="left">
          <DraggableFab
            storageKey="supportInbox:fabPos"
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed', bottom: { xs: 16, md: 24 }, right: { xs: 16, md: 24 }, zIndex: 1200,
              background: `linear-gradient(135deg, ${ACCENT2} 0%, #5c3fd6 100%)`,
              color: '#fff', boxShadow: '0 8px 24px rgba(124,92,255,.35)',
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <SupportAgentIcon />
            </Badge>
          </DraggableFab>
        </Tooltip>
      )}

      <Grow in={open} timeout={180} mountOnEnter unmountOnExit style={{ transformOrigin: 'bottom right' }}>
        <Box
          ref={panelRef}
          role="dialog"
          aria-label="HR support inbox"
          sx={{
            position: 'fixed', zIndex: 2147483000,
            width: 'min(384px, calc(100vw - 28px))',
            height: 'min(620px, calc(100dvh - 32px))', minHeight: 440, maxHeight: 'none',
            top: 'auto', right: 22, bottom: 22, left: 'auto',
            borderRadius: '24px', overflow: 'hidden', bgcolor: '#fff',
            boxShadow: '0 20px 56px rgba(24,34,75,.23), 0 4px 15px rgba(24,34,75,.09)',
            color: '#111f68', fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: '14px', lineHeight: 1.45,
            '& .MuiTypography-root, & .MuiInputBase-root, & .MuiButtonBase-root': { fontFamily: 'inherit' },
            '@media (max-width: 520px)': {
              width: '100vw', height: '100dvh', minHeight: 0, top: 0, right: 0, bottom: 0, borderRadius: 0,
            },
            '@media (max-height: 560px) and (min-width: 521px)': {
              height: 'calc(100dvh - 16px)', minHeight: 0, bottom: 8,
            },
          }}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!activeTicketId && (
            <Box sx={{ minHeight: 82, p: '13px 12px 13px 18px', borderBottom: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, flex: '0 0 auto', borderRadius: '50%', display: 'grid', placeItems: 'center',
                color: '#fff', background: `linear-gradient(135deg, ${ACCENT2} 0%, #5c3fd6 100%)`, border: '2px solid #eeeaff',
              }}>
                <SupportAgentIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.2, fontWeight: 800 }}>Support Inbox</Typography>
                <Typography sx={{ fontSize: '.69rem', color: '#656b85' }}>HR support workspace</Typography>
                <Typography sx={{ mt: .2, fontSize: '.66rem', color: '#9298b2' }}>
                  {unreadCount ? `${unreadCount} unread conversation${unreadCount === 1 ? '' : 's'}` : 'All conversations up to date'}
                </Typography>
              </Box>
              <IconButton onClick={() => setOpen(false)} aria-label="Close support inbox" sx={{ width: 34, height: 34, bgcolor: '#f6f7fc', borderRadius: '10px' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {activeTicketId
              ? <ThreadView ticketId={activeTicketId} onBack={() => setActiveTicketId(null)} onClose={() => setOpen(false)} />
              : <InboxList onOpen={setActiveTicketId} />}
          </Box>
        </Box>
        </Box>
      </Grow>
    </>
  );
}
